/**
 * Client portal « Performances » (W5, B3-ClientPerformance, QW-h2, QW-h10,
 * DES-11, DES-19, DES-20).
 *
 * One period for the whole tab (QW: the period used to drive only part of the
 * page): the period select sets the four figures, the chart and the outcome
 * table alike. The weekly report card (its own week) moved out: it lives on
 * the home tab, whose « Voir » link now opens this tab on 7 days.
 *
 * Figures (VFStatCard, DES-19):
 *   - Appels, RDV obtenus (counts), Taux de conversion = RDV / appels
 *     décrochés (« — » without a connected call);
 *   - « Revenu généré », formatted with Intl in the reader's language, only
 *     when the client's metrics carry a revenue; XDial does not track it
 *     (phase-1 audit F-15), so it otherwise reads « — » « non suivi », never
 *     a made-up 0 € (the former tile said « 0k€ »);
 *   - a trend (and a sparkline) only when « Comparer à la période
 *     précédente » is chosen and that period had calls; rates in points,
 *     counts in %. The « compare with one agent » mode is gone: a team total
 *     against one agent is not a trend.
 * Every call given by the page counts (CallLog and dialer calls), like the
 * Calls tab: the two tabs show the same totals for the same period. No
 * decorative motion or gradient (DES-20).
 * REV-W5-1: while the page's calls load, or when they failed (`load`), the
 * figures are skeletons or « Indisponible » and the chart and the outcome
 * table say so with « Réessayer », never 0 nor « aucun appel »; the revenue
 * tile follows the client metrics' own state (`metricsLoad`).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PerformanceChart from '@/components/backoffice/PerformanceChart';
import { VFStatCard } from '@/components/vf/StatCard';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { currentDateFnsLocale } from '@/i18n/locale';
import { formatRate } from '@/lib/metrics';
import { callDate, callFigures, callOutcome, formatMoney, inRange, isMeeting, READY, type PortalCall, type SectionLoad } from './portalFigures';

interface MetricRow {
  period?: string | null;
  revenue_generated?: number | null;
}

interface PerformanceSectionProps {
  calls?: PortalCall[];
  metrics?: MetricRow[];
  /** Loading / failed state of `calls` (REV-W5-1); ready when absent. */
  load?: SectionLoad;
  /** Loading / failed state of `metrics` (the revenue tile). */
  metricsLoad?: SectionLoad;
}

export const PERIODS = ['7', '30', '90', '365'] as const;
type Period = (typeof PERIODS)[number];
const PERIOD_LABEL: Record<Period, string> = { '7': 'period7d', '30': 'period30d', '90': 'period90d', '365': 'period365d' };

/** Home's « Voir » on the weekly report opens this tab on the last 7 days. */
export const WEEKLY_REPORT_FLAG = 'go_weekly_summary';

function initialPeriod(): Period {
  try {
    if (localStorage.getItem(WEEKLY_REPORT_FLAG)) {
      localStorage.removeItem(WEEKLY_REPORT_FLAG);
      return '7';
    }
  } catch { /* storage unavailable */ }
  return '30';
}

function bounds(days: number, now: Date, offsetPeriods = 0): { start: Date; end: Date } {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  end.setDate(end.getDate() - days * offsetPeriods);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

/** Revenue of the period, or null when no metric row carries one (not tracked). */
export function revenueOf(metrics: MetricRow[], start: Date, end: Date): number | null {
  let tracked = false;
  let sum = 0;
  for (const m of metrics) {
    if (typeof m.revenue_generated !== 'number' || !Number.isFinite(m.revenue_generated)) continue;
    if (m.period) {
      const monthStart = new Date(`${m.period}-01T00:00:00`);
      if (Number.isNaN(monthStart.getTime())) continue;
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59, 999);
      if (monthEnd < start || monthStart > end) continue;
    }
    tracked = true;
    sum += m.revenue_generated;
  }
  return tracked ? sum : null;
}

/** In place of the chart or the table while the calls load or failed (REV-W5-1). */
function NotReady({ load, message, retry }: { load: SectionLoad; message: string; retry: string }) {
  if (load.status === 'loading') {
    return <div className="h-full min-h-16 animate-pulse rounded bg-[var(--vf-surface-3)] motion-reduce:animate-none" role="status" aria-busy="true" />;
  }
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
      <div>
        <p className="text-[var(--vf-text-1)]">{message}</p>
        {load.code && <p className="font-vf-mono text-[11px] text-[var(--vf-text-3)]">{load.code}</p>}
      </div>
      {load.onRetry && <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={load.onRetry}>{retry}</Button>}
    </div>
  );
}

const OUTCOME_ORDER = ['meeting_booked', 'callback_scheduled', 'contacted', 'not_interested', 'no_answer', 'voicemail', 'gatekeeper', 'wrong_number'];

export default function PerformanceSection({ calls = [], metrics = [], load = READY, metricsLoad = READY }: PerformanceSectionProps) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.language;
  const [period, setPeriod] = useState<Period>(initialPeriod);
  const [compare, setCompare] = useState<'none' | 'prev'>('none');

  const days = parseInt(period, 10);
  const now = useMemo(() => new Date(), []);
  const current = useMemo(() => bounds(days, now), [days, now]);
  const previous = useMemo(() => bounds(days, now, 1), [days, now]);

  const inPeriod = useMemo(() => inRange(calls, current.start, current.end), [calls, current]);
  const fig = useMemo(() => callFigures(inPeriod), [inPeriod]);
  const prevRows = useMemo(() => (compare === 'prev' ? inRange(calls, previous.start, previous.end) : []), [calls, previous, compare]);
  // No previous period (or an empty one): no trend at all (DES-19).
  const prev = compare === 'prev' && prevRows.length > 0 ? callFigures(prevRows) : null;

  const daily = useMemo(() => {
    const byDay = new Map<string, { calls: number; meetings: number }>();
    for (const c of inPeriod) {
      const d = callDate(c);
      if (!d) continue;
      const key = format(d, 'yyyy-MM-dd');
      const v = byDay.get(key) ?? { calls: 0, meetings: 0 };
      v.calls += 1;
      if (isMeeting(c)) v.meetings += 1;
      byDay.set(key, v);
    }
    const out: { name: string; calls: number; meetings: number }[] = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(current.start);
      d.setDate(d.getDate() + i);
      const v = byDay.get(format(d, 'yyyy-MM-dd')) ?? { calls: 0, meetings: 0 };
      out.push({ name: format(d, 'dd/MM', { locale: currentDateFnsLocale() }), ...v });
    }
    return out;
  }, [inPeriod, days, current]);

  const outcomes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of inPeriod) {
      const o = isMeeting(c) ? 'meeting_booked' : callOutcome(c) || 'other';
      counts.set(o, (counts.get(o) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => {
      const ia = OUTCOME_ORDER.indexOf(a[0]);
      const ib = OUTCOME_ORDER.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || b[1] - a[1];
    });
  }, [inPeriod]);

  const revenue = revenueOf(metrics, current.start, current.end);
  const periodText = t(`admin:backoffice.performance.${PERIOD_LABEL[period]}`);
  const hint = t('admin:backoffice.performance.hint', { period: periodText });
  const ready = load.status === 'ready';
  const notReady = <NotReady load={load} message={t('admin:backoffice.performance.loadError')} retry={t('common:actions.retry')} />;

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <SelectTrigger className="w-full sm:w-36" aria-label={t('admin:backoffice.performance.periodPlaceholder')}>
            <SelectValue placeholder={t('admin:backoffice.performance.periodPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p} value={p}>{t(`admin:backoffice.performance.${PERIOD_LABEL[p]}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={compare} onValueChange={(v) => setCompare(v as 'none' | 'prev')}>
          <SelectTrigger className="w-full sm:w-52" aria-label={t('admin:backoffice.performance.comparePlaceholder')}>
            <SelectValue placeholder={t('admin:backoffice.performance.comparePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('admin:backoffice.performance.compareNone')}</SelectItem>
            <SelectItem value="prev">{t('admin:backoffice.performance.comparePrev')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <VFStatCard
          state={load.status}
          label={t('admin:backoffice.performance.kpis.callsMade')}
          metric={{ current: fig.calls, previous: prev?.calls ?? null, kind: 'count' }}
          series={daily.map((d) => d.calls)}
          hint={hint}
        />
        <VFStatCard
          state={load.status}
          label={t('admin:backoffice.performance.kpis.meetingsBooked')}
          metric={{ current: fig.meetings, previous: prev?.meetings ?? null, kind: 'count' }}
          series={daily.map((d) => d.meetings)}
          hint={hint}
        />
        <VFStatCard
          state={load.status}
          label={t('admin:backoffice.performance.kpis.conversionRate')}
          metric={{ current: fig.conversion, previous: prev?.conversion ?? null, kind: 'rate' }}
          hint={t('admin:backoffice.performance.conversionHint', { period: periodText, meetings: fig.meetings, connected: fig.connected })}
        />
        <VFStatCard
          state={metricsLoad.status}
          label={t('admin:backoffice.performance.kpis.revenueGenerated')}
          value={revenue === null ? '—' : formatMoney(revenue, locale)}
          deltaLabel={revenue === null ? t('admin:backoffice.performance.notTracked') : undefined}
          hint={revenue === null ? t('admin:backoffice.performance.revenueNotTracked') : hint}
        />
      </div>

      <section className="rounded-vf-lg border p-4 sm:p-6" style={{ borderColor: 'var(--vf-border)' }} aria-labelledby="portal-perf-evolution">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 id="portal-perf-evolution" className="font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.performance.evolution')}</h2>
            <p className="text-sm text-[var(--vf-text-3)]">{t('admin:backoffice.performance.callsAndMeetingsOver', { period: periodText })}</p>
          </div>
          <div className="flex items-center gap-4 text-sm text-[var(--vf-text-2)]">
            <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-purple-500" aria-hidden />{t('admin:backoffice.performance.calls')}</span>
            <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-green-500" aria-hidden />{t('admin:backoffice.performance.meetings')}</span>
          </div>
        </div>
        <div className="h-64 min-w-0">
          {ready ? <PerformanceChart data={fig.calls > 0 ? daily : []} /> : notReady}
        </div>
      </section>

      <section className="rounded-vf-lg border p-4 sm:p-6" style={{ borderColor: 'var(--vf-border)' }} aria-labelledby="portal-perf-outcomes">
        <h2 id="portal-perf-outcomes" className="mb-3 font-semibold text-[var(--vf-text-1)]">{t('admin:backoffice.performance.outcomes.title')}</h2>
        {!ready ? notReady : outcomes.length === 0 ? (
          <p className="text-sm text-[var(--vf-text-3)]">{t('admin:backoffice.performance.outcomes.empty', { period: periodText })}</p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">{t('admin:backoffice.performance.outcomes.title')}</caption>
              <thead>
                <tr className="text-left text-xs text-[var(--vf-text-3)]">
                  <th scope="col" className="py-2 pr-4 font-medium">{t('admin:backoffice.performance.outcomes.outcome')}</th>
                  <th scope="col" className="py-2 px-2 text-right font-medium">{t('admin:backoffice.performance.outcomes.count')}</th>
                  <th scope="col" className="py-2 pl-2 text-right font-medium">{t('admin:backoffice.performance.outcomes.share')}</th>
                </tr>
              </thead>
              <tbody>
                {outcomes.map(([o, n]) => (
                  <tr key={o} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <th scope="row" className="py-2 pr-4 text-left font-normal text-[var(--vf-text-1)]">
                      {o === 'other' ? t('admin:backoffice.performance.outcomes.other') : outcomeLabel(o, t)}
                    </th>
                    <td className="py-2 px-2 text-right tabular-nums">{new Intl.NumberFormat(locale).format(n)}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">{formatRate(n / fig.calls, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
